import { makeObservable, observable, action, runInAction, computed, toJS } from 'mobx';
import {
  getServices,
  getAllEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  linkUserToEmployee,
  unlinkUserFromEmployee,
  getAllUsers
} from '../helpers/api';

class EmployeesManagementStore {
  _employees = [];
  _isLoading = false;
  _error = null;

  // Modal states
  _isModalOpen = false;
  _modalMode = 'create'; // 'create' | 'edit' | 'link' | 'working-hours'
  _currentEmployee = null;

  // Form data
  _formData = {
    name: '',
    nickname: '',
    phone: '',
    services: [],
    servicePrices: [],
    workingHours: [],
    breaks: [],
    timezone: 'America/Sao_Paulo',
    active: true,
    color: '#FF5722'
  };

  // Available options
  _availableServices = [];
  _unlinkedUsers = []; // Users without employee accounts

  constructor() {
    makeObservable(this, {
      _employees: observable,
      _isLoading: observable,
      _error: observable,
      _isModalOpen: observable,
      _modalMode: observable,
      _currentEmployee: observable,
      _formData: observable,
      _availableServices: observable,
      _unlinkedUsers: observable,

      activeEmployees: computed,
      inactiveEmployees: computed,

      fetchEmployees: action,
      fetchServices: action,
      fetchUnlinkedUsers: action,
      createEmployee: action,
      updateEmployee: action,
      deleteEmployee: action,
      toggleEmployeeStatus: action,
      linkUserToEmployee: action,
      unlinkUserFromEmployee: action,

      openModal: action,
      closeModal: action,
      setFormData: action,
      updateFormField: action,
      updateServicePrice: action,
      resetForm: action,
      setError: action,
      clearError: action,

      // Working hours management actions
      addWorkingDay: action,
      removeWorkingDay: action,
      updateWorkingDay: action,
      updateInterval: action,
      addInterval: action,
      removeInterval: action
    });
  }

  // Getters with toJS
  get employees() {
    return toJS(this._employees);
  }

  get isLoading() {
    return this._isLoading;
  }

  get error() {
    return this._error;
  }

  get isModalOpen() {
    return this._isModalOpen;
  }

  get modalMode() {
    return this._modalMode;
  }

  get currentEmployee() {
    return toJS(this._currentEmployee);
  }

  get formData() {
    return toJS(this._formData);
  }

  get availableServices() {
    return toJS(this._availableServices);
  }

  get unlinkedUsers() {
    return toJS(this._unlinkedUsers);
  }

  // Computed getters
  get activeEmployees() {
    return this._employees.filter(emp => emp.active);
  }

  get inactiveEmployees() {
    return this._employees.filter(emp => !emp.active);
  }

  async fetchEmployees() {
    this._isLoading = true;
    this._error = null;

    try {
      const response = await getAllEmployees();
      runInAction(() => {
        this._employees = response.employees || [];
        this._isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Failed to fetch employees';
        this._isLoading = false;
      });
    }
  }

  async fetchServices() {
    try {
      const response = await getServices(false); // Get all services, not just active
      runInAction(() => {
        this._availableServices = response.services || [];
      });
    } catch (err) {
      console.error('Failed to fetch services:', err);
    }
  }

  async fetchUnlinkedUsers() {
    try {
      const response = await getAllUsers({ role: 'employee', unlinked: true });
      runInAction(() => {
        this._unlinkedUsers = response.users || [];
      });
    } catch (err) {
      console.error('Failed to fetch unlinked users:', err);
    }
  }

  async createEmployee(data) {
    this._isLoading = true;
    this._error = null;

    try {
      const response = await createEmployee(data || this._formData);
      runInAction(() => {
        this._employees.push(response.employee);
        this.closeModal();
        this._isLoading = false;
      });
      return response.employee;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Failed to create employee';
        this._isLoading = false;
      });
      throw err;
    }
  }

  async updateEmployee(id, updates) {
    this._isLoading = true;
    this._error = null;

    try {
      const response = await updateEmployee(id, updates);
      runInAction(() => {
        const index = this._employees.findIndex(emp => emp._id === id);
        if (index !== -1) {
          this._employees[index] = response.employee;
        }
        this.closeModal();
        this._isLoading = false;
      });
      return response.employee;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Failed to update employee';
        this._isLoading = false;
      });
      throw err;
    }
  }

  async deleteEmployee(id) {
    if (!window.confirm('Are you sure you want to delete this employee?')) {
      return;
    }

    this._isLoading = true;
    this._error = null;

    try {
      await deleteEmployee(id);
      runInAction(() => {
        this._employees = this._employees.filter(emp => emp._id !== id);
        this._isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Failed to delete employee';
        this._isLoading = false;
      });
    }
  }

  async toggleEmployeeStatus(id) {
    const employee = this._employees.find(emp => emp._id === id);
    if (!employee) return;

    try {
      const response = await updateEmployee(id, {
        active: !employee.active
      });
      runInAction(() => {
        const index = this._employees.findIndex(emp => emp._id === id);
        if (index !== -1) {
          this._employees[index] = response.employee;
        }
      });
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Failed to toggle employee status';
      });
    }
  }

  async linkUserToEmployee(employeeId, userId) {
    this._isLoading = true;
    this._error = null;

    try {
      const response = await linkUserToEmployee(employeeId, userId);
      runInAction(() => {
        const index = this._employees.findIndex(emp => emp._id === employeeId);
        if (index !== -1) {
          this._employees[index] = response.employee;
        }
        // Remove user from unlinked list
        this._unlinkedUsers = this._unlinkedUsers.filter(u => u._id !== userId);
        this.closeModal();
        this._isLoading = false;
      });
      return response.employee;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Failed to link user';
        this._isLoading = false;
      });
      throw err;
    }
  }

  async unlinkUserFromEmployee(employeeId) {
    if (!window.confirm('Are you sure you want to unlink this user account?')) {
      return;
    }

    this._isLoading = true;
    this._error = null;

    try {
      const response = await unlinkUserFromEmployee(employeeId);
      runInAction(() => {
        const index = this._employees.findIndex(emp => emp._id === employeeId);
        if (index !== -1) {
          this._employees[index] = response.employee;
        }
        // Refresh unlinked users
        this.fetchUnlinkedUsers();
        this._isLoading = false;
      });
      return response.employee;
    } catch (err) {
      runInAction(() => {
        this._error = err.message || 'Failed to unlink user';
        this._isLoading = false;
      });
      throw err;
    }
  }

  openModal(mode = 'create', employee = null) {
    this._modalMode = mode;
    this._currentEmployee = employee;

    if (mode === 'edit' && employee) {
      // Populate form with employee data
      this._formData = {
        name: employee.name || '',
        nickname: employee.nickname || '',
        phone: employee.phone || '',
        services: employee.services || [],
        servicePrices: employee.servicePrices || [],
        workingHours: employee.workingHours || [],
        breaks: employee.breaks || [],
        timezone: employee.timezone || 'America/Sao_Paulo',
        active: employee.active !== false,
        color: employee.color || '#FF5722'
      };
    } else if (mode === 'working-hours' && employee) {
      // Only populate working hours for working-hours modal
      this._formData = {
        ...this._formData,
        workingHours: employee.workingHours || []
      };
    } else if (mode === 'create') {
      this.resetForm();
    }

    if (mode === 'link') {
      this.fetchUnlinkedUsers();
    } else if (mode === 'create' || mode === 'edit') {
      // Fetch services when opening create/edit modal
      this.fetchServices();
    }

    this._isModalOpen = true;
  }

  closeModal() {
    this._isModalOpen = false;
    this._currentEmployee = null;
    this.resetForm();
    this.clearError();
  }

  setFormData(data) {
    this._formData = { ...this._formData, ...data };
  }

  updateFormField(field, value) {
    this._formData[field] = value;

    // When services change, clean up servicePrices for removed services
    if (field === 'services') {
      this._formData.servicePrices = this._formData.servicePrices.filter(
        sp => value.includes(sp.serviceId)
      );
    }
  }

  /**
   * Update or remove a service price override for the current employee form
   * @param {string} serviceId - Service ID
   * @param {string|number|null} price - Price value (null/empty to remove override)
   */
  updateServicePrice(serviceId, price) {
    const existing = this._formData.servicePrices.findIndex(sp => sp.serviceId === serviceId);

    if (price === null || price === '' || price === undefined) {
      // Remove override
      if (existing !== -1) {
        this._formData.servicePrices.splice(existing, 1);
      }
    } else {
      const numPrice = parseFloat(price);
      if (isNaN(numPrice) || numPrice <= 0) return;

      if (existing !== -1) {
        this._formData.servicePrices[existing].price = numPrice;
      } else {
        this._formData.servicePrices.push({ serviceId, price: numPrice });
      }
    }
  }

  resetForm() {
    this._formData = {
      name: '',
      nickname: '',
      phone: '',
      services: [],
      servicePrices: [],
      workingHours: [],
      breaks: [],
      timezone: 'America/Sao_Paulo',
      active: true,
      color: '#FF5722'
    };
  }

  setError(error) {
    this._error = error;
  }

  clearError() {
    this._error = null;
  }

  // Working Hours Management Methods
  addWorkingDay() {
    this._formData.workingHours = [
      ...this._formData.workingHours,
      { day: 1, intervals: [{ start: '09:00', end: '18:00' }] }
    ];
  }

  removeWorkingDay(index) {
    this._formData.workingHours.splice(index, 1);
  }

  updateWorkingDay(index, field, value) {
    this._formData.workingHours[index] = {
      ...this._formData.workingHours[index],
      [field]: value
    };
  }

  updateInterval(dayIndex, intervalIndex, field, value) {
    const intervals = [...this._formData.workingHours[dayIndex].intervals];
    intervals[intervalIndex] = {
      ...intervals[intervalIndex],
      [field]: value
    };
    this._formData.workingHours[dayIndex].intervals = intervals;
  }

  addInterval(dayIndex) {
    this._formData.workingHours[dayIndex].intervals.push({
      start: '09:00',
      end: '18:00'
    });
  }

  removeInterval(dayIndex, intervalIndex) {
    this._formData.workingHours[dayIndex].intervals.splice(intervalIndex, 1);
  }

  // Initialize store
  async init() {
    await this.fetchEmployees();
  }

  // Cleanup
  dispose() {
    // Reset all observables
    this._employees = [];
    this._availableServices = [];
    this._unlinkedUsers = [];
    this._isLoading = false;
    this._error = null;
    this.closeModal();
  }
}

const employeesManagementStore = new EmployeesManagementStore();
export default employeesManagementStore;